import { useState } from 'react';
import {
  Stack,
  TextInput,
  Select,
  MultiSelect,
  Group,
  Button,
  ActionIcon,
  Badge,
} from '@mantine/core';
import {
  IconSearch,
  IconX,
  IconChevronDown,
  IconChevronUp,
  IconFilter,
  IconUser,
  IconBook2,
} from '@tabler/icons-react';
import { ResponsiveSelect } from '../UI/ResponsiveSelect';
import { useIsBottomSheet } from '../UI/ResponsiveDrawer';

export interface RecipeSearchFiltersProps {
  // Search state
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  
  // Filter state
  selectedCuisines: string[];
  selectedDifficulty: string;
  selectedDietaryRestrictions: string[];
  selectedPrepTime: string;
  selectedTotalTime: string;
  selectedOrigin?: string;
  // Meal filter (slots-first): rendered only when the change handler is
  // provided - the swap drawer carries its own meal control
  selectedMeal?: string;
  showNeedsReviewOnly?: boolean;

  // Options
  cuisineOptions: string[];
  dietaryOptions: string[];

  // Filter callbacks
  onSelectedCuisinesChange: (cuisines: string[]) => void;
  onSelectedDifficultyChange: (difficulty: string) => void;
  onSelectedDietaryRestrictionsChange: (restrictions: string[]) => void;
  onSelectedPrepTimeChange: (prepTime: string) => void;
  onSelectedTotalTimeChange: (totalTime: string) => void;
  onSelectedOriginChange?: (origin: string) => void;
  onSelectedMealChange?: (meal: string) => void;
  onShowNeedsReviewOnlyChange?: (showNeedsReview: boolean) => void;
  onClearFilters: () => void;

  // Configuration
  showCollapsible?: boolean;
  showNeedsReviewFilter?: boolean;
  showClearAllButton?: boolean;
  defaultExpanded?: boolean;
  searchPlaceholder?: string;
}

// Custom render function for origin select options
const renderOriginSelectOption = ({ option }: { option: { value: string; label: string } }) => (
  <Badge
    size="sm"
    variant="light"
    color={option.value === 'ai_generated' ? 'orange' : 'teal'}
    leftSection={option.value === 'ai_generated' ? <IconBook2 size={12} /> : <IconUser size={12} />}
    style={{ cursor: 'pointer' }}
  >
    {option.label}
  </Badge>
);


export const RecipeSearchFilters = ({
  // Search state
  searchQuery,
  onSearchQueryChange,
  
  // Filter state
  selectedCuisines,
  selectedDifficulty,
  selectedMeal = '',
  selectedDietaryRestrictions,
  selectedPrepTime,
  selectedTotalTime,
  selectedOrigin = '',
  showNeedsReviewOnly = false,

  // Options
  cuisineOptions,
  dietaryOptions,

  // Filter callbacks
  onSelectedCuisinesChange,
  onSelectedDifficultyChange,
  onSelectedMealChange,
  onSelectedDietaryRestrictionsChange,
  onSelectedPrepTimeChange,
  onSelectedTotalTimeChange,
  onSelectedOriginChange,
  onShowNeedsReviewOnlyChange,
  onClearFilters,

  // Configuration
  showCollapsible = true,
  showNeedsReviewFilter = false,
  showClearAllButton = true,
  defaultExpanded = false,
  searchPlaceholder = "Search recipes by name, description, or cuisine...",
}: RecipeSearchFiltersProps) => {
  const [filtersExpanded, setFiltersExpanded] = useState(defaultExpanded);
  // These filters render inside RecipeSelectionDrawer's bottom sheet on
  // small screens; floating dropdowns must not portal to the body there.
  const isBottomSheet = useIsBottomSheet();

  // Check if any advanced filters are active
  const hasActiveAdvancedFilters = selectedCuisines.length > 0 || 
    selectedDietaryRestrictions.length > 0 || 
    selectedDifficulty !== '' || 
    selectedPrepTime !== '' || 
    selectedTotalTime !== '' ||
    selectedOrigin !== '' ||
    selectedMeal !== '' ||
    (showNeedsReviewFilter && showNeedsReviewOnly);

  const activeFilterCount = [
    selectedCuisines.length,
    selectedDietaryRestrictions.length,
    selectedDifficulty ? 1 : 0,
    selectedPrepTime ? 1 : 0,
    selectedTotalTime ? 1 : 0,
    selectedOrigin ? 1 : 0,
    selectedMeal ? 1 : 0,
    showNeedsReviewFilter && showNeedsReviewOnly ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  const renderFilters = () => (
    <>
      {/* Desktop: Filters in a row */}
      <Group gap="md" align="flex-end" visibleFrom="sm">
        <MultiSelect
          label="Cuisine"
          placeholder="Any cuisine"
          data={cuisineOptions}
          value={selectedCuisines}
          onChange={onSelectedCuisinesChange}
          clearable
          size="sm"
          style={{ minWidth: 150 }}
        />
        
        <MultiSelect
          label="Dietary"
          placeholder="Any restrictions"
          data={dietaryOptions}
          value={selectedDietaryRestrictions}
          onChange={onSelectedDietaryRestrictionsChange}
          clearable
          size="sm"
          style={{ minWidth: 150 }}
        />
        
        {onSelectedMealChange && (
          <Select
            label="Meal"
            placeholder="Any meal"
            data={[
              { value: 'breakfast', label: 'Breakfast' },
              { value: 'lunch', label: 'Lunch' },
              { value: 'dinner', label: 'Dinner' }
            ]}
            value={selectedMeal}
            onChange={(value) => onSelectedMealChange(value || '')}
            clearable
            size="sm"
            style={{ minWidth: 120 }}
          />
        )}

        <Select
          label="Difficulty"
          placeholder="Any difficulty"
          data={[
            { value: 'easy', label: 'Easy' },
            { value: 'medium', label: 'Medium' },
            { value: 'hard', label: 'Hard' }
          ]}
          value={selectedDifficulty}
          onChange={(value) => onSelectedDifficultyChange(value || '')}
          clearable
          size="sm"
          style={{ minWidth: 120 }}
        />
        
        <Select
          label="Prep time"
          placeholder="Any prep time"
          data={[
            { value: 'quick', label: '≤ 15 min' },
            { value: 'medium', label: '15-30 min' },
            { value: 'long', label: '> 30 min' }
          ]}
          value={selectedPrepTime}
          onChange={(value) => onSelectedPrepTimeChange(value || '')}
          clearable
          size="sm"
          style={{ minWidth: 130 }}
        />
        
        <Select
          label="Total time"
          placeholder="Any total time"
          data={[
            { value: 'quick', label: '≤ 30 min' },
            { value: 'medium', label: '30-60 min' },
            { value: 'long', label: '> 60 min' }
          ]}
          value={selectedTotalTime}
          onChange={(value) => onSelectedTotalTimeChange(value || '')}
          clearable
          size="sm"
          style={{ minWidth: 130 }}
        />
        
        {onSelectedOriginChange && (
          <Select
            label="Source"
            placeholder="Any source"
            data={[
              { value: 'user_recipe', label: 'My Recipes' },
              { value: 'ai_generated', label: 'From Meals You Love' }
            ]}
            renderOption={renderOriginSelectOption}
                                    value={selectedOrigin}
            onChange={(value) => onSelectedOriginChange(value || '')}
            clearable
            size="sm"
            style={{ minWidth: 120 }}
          />
        )}
        
        {showNeedsReviewFilter && onShowNeedsReviewOnlyChange && (
          <Button
            variant={showNeedsReviewOnly ? "filled" : "light"}
            size="sm"
            onClick={() => onShowNeedsReviewOnlyChange(!showNeedsReviewOnly)}
            style={{ minWidth: 100 }}
          >
            Needs review
          </Button>
        )}
        
        {showClearAllButton && (
          <Button
            variant="light"
            size="sm"
            onClick={onClearFilters}
            disabled={!searchQuery && !hasActiveAdvancedFilters}
          >
            Clear all
          </Button>
        )}
      </Group>
      
      {/* Mobile: Filters stacked */}
      <Stack gap="sm" hiddenFrom="sm">
        <Group gap="sm">
          <MultiSelect
            label="Cuisine"
            placeholder="Any cuisine"
            data={cuisineOptions}
            value={selectedCuisines}
            onChange={onSelectedCuisinesChange}
            clearable
            size="sm"
            style={{ flex: 1 }}
            comboboxProps={{ withinPortal: !isBottomSheet }}
          />
          <MultiSelect
            label="Dietary"
            placeholder="Any restrictions"
            data={dietaryOptions}
            value={selectedDietaryRestrictions}
            onChange={onSelectedDietaryRestrictionsChange}
            clearable
            size="sm"
            style={{ flex: 1 }}
            comboboxProps={{ withinPortal: !isBottomSheet }}
          />
        </Group>

        <Group gap="sm">
          <ResponsiveSelect
            label="Difficulty"
            placeholder="Any difficulty"
            data={[
              { value: 'easy', label: 'Easy' },
              { value: 'medium', label: 'Medium' },
              { value: 'hard', label: 'Hard' }
            ]}
            value={selectedDifficulty}
            onChange={(value) => onSelectedDifficultyChange(value || '')}
            clearable
            size="sm"
            style={{ flex: 1 }}
          />
          <ResponsiveSelect
            label="Prep time"
            placeholder="Any prep time"
            data={[
              { value: 'quick', label: '≤ 15 min' },
              { value: 'medium', label: '15-30 min' },
              { value: 'long', label: '> 30 min' }
            ]}
            value={selectedPrepTime}
            onChange={(value) => onSelectedPrepTimeChange(value || '')}
            clearable
            size="sm"
            style={{ flex: 1 }}
          />
        </Group>

        <Group gap="sm" align="flex-end">
          <ResponsiveSelect
            label="Total time"
            placeholder="Any total time"
            data={[
              { value: 'quick', label: '≤ 30 min' },
              { value: 'medium', label: '30-60 min' },
              { value: 'long', label: '> 60 min' }
            ]}
            value={selectedTotalTime}
            onChange={(value) => onSelectedTotalTimeChange(value || '')}
            clearable
            size="sm"
            style={{ flex: 1 }}
          />
          {onSelectedOriginChange && (
            <ResponsiveSelect
              label="Source"
              placeholder="Any source"
              data={[
                { value: 'user_recipe', label: 'My Recipes' },
                { value: 'ai_generated', label: 'From Meals You Love' }
              ]}
              renderOption={renderOriginSelectOption}
              value={selectedOrigin}
              onChange={(value) => onSelectedOriginChange(value || '')}
              clearable
              size="sm"
              style={{ flex: 1 }}
            />
          )}
        </Group>
        
        {(showNeedsReviewFilter && onShowNeedsReviewOnlyChange) && (
          <Group gap="sm">
            <Button
              variant={showNeedsReviewOnly ? "filled" : "light"}
              size="sm"
              onClick={() => onShowNeedsReviewOnlyChange(!showNeedsReviewOnly)}
              fullWidth
            >
              Needs review
            </Button>
          </Group>
        )}
        
        {showClearAllButton && (
          <Button
            variant="light"
            size="sm"
            onClick={onClearFilters}
            disabled={!searchQuery && !hasActiveAdvancedFilters}
            fullWidth
          >
            Clear all
          </Button>
        )}
      </Stack>
    </>
  );

  if (!showCollapsible) {
    // Non-collapsible version
    return (
      <Stack gap="md">
        {/* Search */}
        <TextInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchQuery ? (
              <IconX 
                size={16} 
                style={{ cursor: 'pointer' }} 
                onClick={() => onSearchQueryChange('')}
              />
            ) : null
          }
        />
        
        {/* Filters */}
        {renderFilters()}
      </Stack>
    );
  }

  // Collapsible version (for main pages)
  return (
    <Stack gap="md">
      {/* Desktop: Search and expandable filters button */}
      <Group gap="md" align="flex-end" visibleFrom="sm">
        <TextInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchQuery ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => onSearchQueryChange('')}
                size="sm"
              >
                <IconX size={16} />
              </ActionIcon>
            ) : null
          }
          style={{ flex: 1 }}
        />
        
        <Button
          variant="light"
          size="sm"
          leftSection={<IconFilter size={16} />}
          rightSection={filtersExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          style={{ minWidth: 150 }}
        >
          Filters{hasActiveAdvancedFilters && ` (${activeFilterCount})`}
        </Button>
      </Group>
      
      {/* Mobile: Search and filters stacked */}
      <Stack gap="sm" hiddenFrom="sm">
        <TextInput
          placeholder="Search recipes..."
          value={searchQuery}
          onChange={(event) => onSearchQueryChange(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchQuery ? (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => onSearchQueryChange('')}
                size="sm"
              >
                <IconX size={16} />
              </ActionIcon>
            ) : null
          }
        />
        
        <Group>
          <Button
            variant="light"
            size="sm"
            leftSection={<IconFilter size={16} />}
            rightSection={filtersExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            Filters{hasActiveAdvancedFilters && ` (${activeFilterCount})`}
          </Button>
        </Group>
      </Stack>
      
      {/* Advanced Filters - Collapsible */}
      {filtersExpanded && (
        <Stack gap="lg">
          {renderFilters()}
        </Stack>
      )}
    </Stack>
  );
};
